import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { useParams } from "react-router-dom"
import { BROADCAST_STATUS, broadcastKeys, getBroadcast, watchBroadcast } from "@/entities/broadcast"
import { useSessionRole } from "@/entities/session"
import { rememberRecentItem } from "@/shared/lib/recent-items"
import { DraftEditor } from "@/features/edit-broadcast-draft"
import { AccessDenied } from "@/shared/ui/access-denied"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { Skeleton } from "@/shared/ui/skeleton"
import { WorkspaceSection } from "@/widgets/bot-workspace"
import { BroadcastReport } from "@/widgets/broadcast-report"
import { LoadSwap } from "@/shared/ui/load-swap"

/** Рассылка: черновик с формой или отчёт об отправке. */
export function BroadcastPage() {
	const { botId = "", broadcastId = "" } = useParams()
	const { isModerator } = useSessionRole()
	const query = useQuery({
		queryKey: broadcastKeys.detail(botId, broadcastId),
		queryFn: () => getBroadcast(botId, broadcastId),
		enabled: isModerator && !!botId && !!broadcastId,
		refetchInterval: (current) =>
			current.state.data?.status === "SENDING" || current.state.data?.status === "SCHEDULED"
				? 3000
				: false,
	})

	const status = query.data?.status
	const name = query.data?.name ?? ""
	useEffect(() => {
		if (status === "SENDING" || status === "SCHEDULED")
			watchBroadcast({ botId, id: broadcastId, name })
	}, [status, botId, broadcastId, name])
	useEffect(() => {
		if (!name) return
		rememberRecentItem({
			kind: "broadcast",
			id: broadcastId,
			label: name,
			to: `/admin/bots/${botId}/broadcasts/${broadcastId}`,
		})
	}, [botId, broadcastId, name])

	if (!isModerator) return <AccessDenied title="Рассылка" />
	const broadcast = query.data

	return (
		<LoadSwap
			loading={query.isLoading}
			id={!broadcast ? "error" : broadcast.status === "DRAFT" ? "draft" : "report"}
		>
			{query.isLoading ? (
				<WorkspaceSection title={null} eyebrow="Рассылка">
					<BroadcastSkeleton />
				</WorkspaceSection>
			) : !broadcast ? (
				<WorkspaceSection title="Рассылка">
					<LoadErrorState onRetry={() => void query.refetch()} />
				</WorkspaceSection>
			) : broadcast.status === "DRAFT" ? (
				<DraftEditor
					key={broadcast.id}
					botId={botId}
					broadcast={broadcast}
					shell={({ eyebrow, title, actions, children }) => (
						<WorkspaceSection eyebrow={eyebrow} title={title} actions={actions}>
							{children}
						</WorkspaceSection>
					)}
				/>
			) : (
				<WorkspaceSection
					eyebrow={BROADCAST_STATUS[broadcast.status].label}
					title={broadcast.name || "Рассылка"}
				>
					<BroadcastReport botId={botId} broadcast={broadcast} />
				</WorkspaceSection>
			)}
		</LoadSwap>
	)
}

/** Заглушка черновика: сообщение, получатели и время слева, превью и сводка справа. */
function BroadcastSkeleton() {
	return (
		<div aria-hidden="true" className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
			<div className="space-y-4">
				{[520, 420, 132].map((height) => (
					<div key={height} className="rounded-2xl border border-border bg-card p-5" style={{ height }}>
						<Skeleton className="h-4 w-28 rounded" />
						<Skeleton className="mt-2 h-3 w-64 max-w-full rounded" />
						<Skeleton className="mt-6 h-[calc(100%-5rem)] w-full rounded-xl" />
					</div>
				))}
			</div>
			<div className="space-y-3">
				<Skeleton className="h-48 w-full rounded-2xl" />
				<Skeleton className="h-56 w-full rounded-2xl" />
			</div>
		</div>
	)
}
